import React, { useCallback, useMemo, useState } from 'react';
import { CalendarIcon } from 'lucide-react';

import { useOrderSourcesQuery } from 'components/logistics/fulfillmentLifecycle/hooks/useOrderSourcesQuery';
import { Button } from 'shadcn/components/ui/Button';
import { Calendar } from 'shadcn/components/ui/Calendar';
import { MultiSelect } from 'shadcn/components/ui/MultiSelect';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { cn } from 'shadcn/lib/utils';
import dayjs from 'utils/dayjsConfig';

const GROUP_PREFIX = 'group:';

const DELIVERY_TYPE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'DBI', label: 'DBI' },
    { value: 'CARRIER', label: 'Carrier' },
];

const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'critical', label: 'Critical' },
    { value: 'atRisk', label: 'At Risk' },
    { value: 'onTrack', label: 'On Track' },
];

const MILESTONE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'labels', label: 'Labels' },
    { value: 'packing', label: 'Packing' },
    { value: 'loading', label: 'Loading' },
    { value: 'complete', label: 'Complete' },
];

/**
 * FilterControls
 *
 * Filter bar for the Fulfillment Lifecycle Dashboard.
 * Contains date picker, order source, delivery type, status filter, and milestone selectors.
 *
 * @param {Object} props
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD)
 * @param {Function} props.onDateChange - Callback when date changes
 * @param {Object} props.filters - Current filter state
 * @param {string[]} props.filters.orderSources - Selected order sources
 * @param {string[]} props.filters.deliveryTypes - Selected delivery types
 * @param {string} props.filters.statusFilter - Selected status filter
 * @param {string} props.filters.milestone - Selected milestone filter
 * @param {Function} props.onFilterChange - Callback when any filter changes: (filterKey, value) => void
 */
function FilterControls({ filters, onDateChange, onFilterChange, selectedDate }) {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const sourceData = useOrderSourcesQuery();

    const orderSourceSelectOptions = useMemo(() => {
        const validSourceValues = new Set(
            sourceData.sources
                .filter((s) => typeof s.value === 'string' && typeof s.label === 'string')
                .map((s) => s.value),
        );

        const groupOptions = sourceData.groups
            .filter(
                (group) =>
                    typeof group.key === 'string' &&
                    typeof group.label === 'string' &&
                    Array.isArray(group.values) &&
                    group.values.some((v) => validSourceValues.has(v)),
            )
            .map((group) => ({
                value: `${GROUP_PREFIX}${group.key}`,
                label: group.label,
            }));

        const sourceOptions = sourceData.sources
            .filter((source) => typeof source.value === 'string' && typeof source.label === 'string')
            .map((source) => ({
                value: source.value,
                label: source.label,
            }));

        return [...groupOptions, ...sourceOptions];
    }, [sourceData]);

    const sourceValueSet = useMemo(
        () => new Set(sourceData.sources.map((source) => source.value)),
        [sourceData.sources],
    );
    const groupValuesByToken = useMemo(() => {
        const map = new Map();
        sourceData.groups.forEach((group) => {
            const groupToken = `${GROUP_PREFIX}${group.key}`;
            const groupValues = group.values.filter((value) => sourceValueSet.has(value));
            if (groupValues.length > 0) {
                map.set(groupToken, groupValues);
            }
        });
        return map;
    }, [sourceData.groups, sourceValueSet]);

    const { checkedOrderSourceValues, selectedOrderSourceValues } = useMemo(() => {
        const selected = Array.isArray(filters.orderSources) ? filters.orderSources : [];
        const selectedSourceValues = selected.filter((value) => sourceValueSet.has(value));
        const selectedSourceSet = new Set(selectedSourceValues);

        const coveredByGroup = new Set();
        const groupTokens = [];

        sourceData.groups.forEach((group) => {
            const validGroupValues = group.values.filter((value) => sourceValueSet.has(value));
            if (validGroupValues.length > 0 && validGroupValues.every((value) => selectedSourceSet.has(value))) {
                groupTokens.push(`${GROUP_PREFIX}${group.key}`);
                validGroupValues.forEach((value) => coveredByGroup.add(value));
            }
        });

        const ungroupedSelections = selectedSourceValues.filter((value) => !coveredByGroup.has(value));

        return {
            selectedOrderSourceValues: [...groupTokens, ...ungroupedSelections],
            checkedOrderSourceValues: [...groupTokens, ...selectedSourceValues],
        };
    }, [filters.orderSources, sourceData.groups, sourceValueSet]);

    const handleToggleOrderSource = useCallback(
        (toggledValue) => {
            const currentCanonical = new Set(
                (Array.isArray(filters.orderSources) ? filters.orderSources : []).filter((v) => sourceValueSet.has(v)),
            );

            const groupValues = groupValuesByToken.get(toggledValue);
            if (groupValues) {
                const allSelected = groupValues.every((v) => currentCanonical.has(v));
                if (allSelected) {
                    groupValues.forEach((v) => currentCanonical.delete(v));
                } else {
                    groupValues.forEach((v) => currentCanonical.add(v));
                }
            } else if (sourceValueSet.has(toggledValue)) {
                if (currentCanonical.has(toggledValue)) {
                    currentCanonical.delete(toggledValue);
                } else {
                    currentCanonical.add(toggledValue);
                }
            }

            onFilterChange(
                'orderSources',
                [...currentCanonical].sort((a, b) => a.localeCompare(b)),
            );
        },
        [filters.orderSources, groupValuesByToken, onFilterChange, sourceValueSet],
    );

    const translateToCanonical = useCallback(
        (values) => {
            const canonicalSet = new Set();
            values.forEach((value) => {
                if (typeof value !== 'string') return;
                const groupValues = groupValuesByToken.get(value);
                if (groupValues) {
                    groupValues.forEach((v) => canonicalSet.add(v));
                } else if (sourceValueSet.has(value)) {
                    canonicalSet.add(value);
                }
            });
            return [...canonicalSet].sort((a, b) => a.localeCompare(b));
        },
        [groupValuesByToken, sourceValueSet],
    );

    const handleOrderSourceChange = useCallback(
        (values) => {
            if (!Array.isArray(values) || values.length === 0) {
                onFilterChange('orderSources', []);
                return;
            }
            onFilterChange('orderSources', translateToCanonical(values));
        },
        [onFilterChange, translateToCanonical],
    );

    const handleClearExtraOrderSources = useCallback(
        (currentValues, maxCount) => {
            const kept = currentValues.slice(0, maxCount);
            onFilterChange('orderSources', translateToCanonical(kept));
        },
        [onFilterChange, translateToCanonical],
    );

    // Parse selectedDate string to Date for the Calendar (use dayjs for consistency with dashboard init)
    const selectedDateObj = selectedDate ? dayjs(selectedDate).toDate() : dayjs().toDate();

    const formattedDate = selectedDateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });

    const handleDateSelect = (date) => {
        if (date) {
            onDateChange(dayjs(date).format('YYYY-MM-DD'));
            setCalendarOpen(false);
        }
    };

    const handleTodayClick = () => {
        onDateChange(dayjs().format('YYYY-MM-DD'));
    };

    // Derive single delivery type value for the select
    const deliveryTypeValue =
        filters.deliveryTypes && filters.deliveryTypes.length === 1 ? filters.deliveryTypes[0] : 'all';

    const handleDeliveryTypeChange = (value) => {
        onFilterChange('deliveryTypes', value === 'all' ? [] : [value]);
    };

    return (
        <div className="flex flex-wrap items-end gap-3">
            {/* Operating Date */}
            <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operating Date</span>
                <div className="flex gap-1.5">
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    'w-[220px] justify-start text-left font-normal',
                                    !selectedDate && 'text-muted-foreground',
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formattedDate}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={selectedDateObj}
                                onSelect={handleDateSelect}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="sm" onClick={handleTodayClick}>
                        Today
                    </Button>
                </div>
            </label>

            {/* Order Source */}
            {orderSourceSelectOptions.length > 0 && (
                <label className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">Order Source</span>
                    <MultiSelect
                        className="w-[220px] text-foreground border-input bg-background hover:bg-background"
                        options={orderSourceSelectOptions}
                        onValueChange={handleOrderSourceChange}
                        onToggleOption={handleToggleOrderSource}
                        onClearExtra={handleClearExtraOrderSources}
                        value={selectedOrderSourceValues}
                        checkedValues={checkedOrderSourceValues}
                        placeholder="All Sources"
                        placeholderClassName="text-foreground"
                        chevronClassName="text-foreground"
                        maxCount={2}
                    />
                </label>
            )}

            {/* Delivery Type */}
            <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Delivery Type</span>
                <Select value={deliveryTypeValue} onValueChange={handleDeliveryTypeChange}>
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {DELIVERY_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </label>

            {/* Status Filter */}
            <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Status Filter</span>
                <Select
                    value={filters.statusFilter || 'all'}
                    onValueChange={(value) => onFilterChange('statusFilter', value)}
                >
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </label>

            {/* Milestone */}
            <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Milestone</span>
                <Select
                    value={filters.milestone || 'all'}
                    onValueChange={(value) => onFilterChange('milestone', value)}
                >
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {MILESTONE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </label>
        </div>
    );
}

export default FilterControls;
